import { createSignal } from "solid-js";

import localforage from "localforage";

/**
 * Storage key for the wallpaper image (kept apart from the JSON stores)
 */
const STORAGE_KEY = "typus:wallpaper";

/**
 * Largest accepted wallpaper, matches the server limit for backgrounds
 */
export const MAX_WALLPAPER_SIZE = 6_000_000;

const [wallpaperUrl, setWallpaperUrl] = createSignal<string>();

/**
 * Object URL of the current wallpaper, if one is set
 */
export { wallpaperUrl };

/**
 * URL of the wallpaper to display: the synced file on the server when there
 * is one, otherwise the copy saved in this browser
 * @param fileId Server-side file id (Autumn "backgrounds")
 * @param mediaUrl Autumn base URL
 * @returns Wallpaper URL
 */
export function activeWallpaperUrl(fileId?: string, mediaUrl?: string) {
  return fileId && mediaUrl
    ? `${mediaUrl}/backgrounds/${fileId}`
    : wallpaperUrl();
}

/**
 * Replace the displayed wallpaper, freeing the previous object URL
 * @param image Image to display or nothing to clear
 */
function display(image?: Blob | null) {
  const previous = wallpaperUrl();
  if (previous) URL.revokeObjectURL(previous);
  setWallpaperUrl(image ? URL.createObjectURL(image) : undefined);
}

/**
 * Load the saved wallpaper from browser storage
 */
export async function loadWallpaper() {
  try {
    display(await localforage.getItem<Blob>(STORAGE_KEY));
  } catch {
    display(null);
  }
}

/**
 * Save and display a new wallpaper
 * @param image Image file chosen by the user
 * @throws If the file is not an image or is too large
 */
export async function saveWallpaper(image: File) {
  if (!image.type.startsWith("image/")) {
    throw new Error("NotAnImage");
  }

  if (image.size > MAX_WALLPAPER_SIZE) {
    throw new Error("FileTooLarge");
  }

  await localforage.setItem(STORAGE_KEY, image);
  display(image);
}

/**
 * Remove the saved wallpaper
 */
export async function removeWallpaper() {
  await localforage.removeItem(STORAGE_KEY);
  display(null);
}
